import Cookies from 'js-cookie';
import { AsyncStorage } from 'oidc-client-ts';

/**
 * Cookie-based storage implementation that fulfills the Storage interface.
 * This can be used as a drop-in replacement for localStorage or sessionStorage
 * when you want to store data in cookies instead.
 */
export class CookieStorage implements Storage {
  private readonly keyPrefix: string;
  private readonly cookieOptions: Cookies.CookieAttributes;

  constructor(
    options: {
      keyPrefix?: string;
      expires?: number | Date;
      domain?: string;
      path?: string;
      secure?: boolean;
      sameSite?: 'strict' | 'lax' | 'none';
    } = {}
  ) {
    this.keyPrefix = options.keyPrefix || 'nf_';
    this.cookieOptions = {
      expires: options.expires || 7,
      domain: options.domain,
      path: options.path || '/',
      secure: options.secure,
      sameSite: options.sameSite || 'lax'
    };
  }

  getItem(key: string): string | null {
    const prefixedKey = this.getPrefixedKey(key);
    return Cookies.get(prefixedKey) ?? null;
  }

  setItem(key: string, value: string): void {
    const prefixedKey = this.getPrefixedKey(key);
    Cookies.set(prefixedKey, value, this.cookieOptions);
  }

  removeItem(key: string): void {
    const prefixedKey = this.getPrefixedKey(key);
    Cookies.remove(prefixedKey, {
      domain: this.cookieOptions.domain,
      path: this.cookieOptions.path
    });
  }

  /**
   * Removes all key/value pairs managed by this storage instance.
   * Note: This only removes cookies with the configured prefix.
   */
  clear(): void {
    const allKeys = this.getAllKeys();
    allKeys.forEach(key => {
      Cookies.remove(key, {
        domain: this.cookieOptions.domain,
        path: this.cookieOptions.path
      });
    });
  }

  /**
   * Returns the number of key/value pairs managed by this storage instance.
   */
  get length(): number {
    return this.getAllKeys().length;
  }

  /**
   * Returns the name of the nth key, or null
   * if n is greater than or equal to the number of key/value pairs.
   */
  key(index: number): string | null {
    const keys = this.getAllKeys();
    if (index < 0 || index >= keys.length) {
      return null;
    }
    // Return the key without the prefix
    const prefixedKey = keys[index];
    return this.removePrefixFromKey(prefixedKey);
  }

  /**
   * Get the prefixed key for internal cookie storage
   */
  private getPrefixedKey(key: string): string {
    return `${this.keyPrefix}${key}`;
  }

  /**
   * Remove the prefix from a key to get the original key
   */
  private removePrefixFromKey(prefixedKey: string): string {
    return prefixedKey.startsWith(this.keyPrefix)
      ? prefixedKey.slice(this.keyPrefix.length)
      : prefixedKey;
  }

  /**
   * Get all cookie keys that belong to this storage instance (have the prefix)
   */
  private getAllKeys(): string[] {
    // Get all cookies and filter by prefix
    const allCookies = Cookies.get();
    return Object.keys(allCookies).filter(key => key.startsWith(this.keyPrefix));
  }
}

/**
 * Cookie-based async storage implementation that fulfills the AsyncStorage interface.
 * This can be used when you need an async storage interface.
 */
export class CookieAsyncStorage implements AsyncStorage {
  private readonly cookieStorage: CookieStorage;

  constructor(
    options: {
      keyPrefix?: string;
      expires?: number | Date;
      domain?: string;
      path?: string;
      secure?: boolean;
      sameSite?: 'strict' | 'lax' | 'none';
    } = {}
  ) {
    this.cookieStorage = new CookieStorage(options);
  }

  async getItem(key: string): Promise<string | null> {
    return this.cookieStorage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    this.cookieStorage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    this.cookieStorage.removeItem(key);
  }

  async clear(): Promise<void> {
    this.cookieStorage.clear();
  }

  async key(index: number): Promise<string | null> {
    return this.cookieStorage.key(index);
  }

  get length(): Promise<number> {
    return Promise.resolve(this.cookieStorage.length);
  }
}
